import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';

export const fetchLakes = createAsyncThunk(
  'lakes/fetchLakes',
  async (searchParams) => {
    const lakesRef = collection(db, 'lakes');
    let constraints = [];

    // Note: name search is handled client-side for case-insensitive matching
    if (searchParams.stationId) {
      constraints.push(where('stationId', '==', searchParams.stationId));
    }
    if (searchParams.lakeType && searchParams.lakeType.length > 0) {
      if (Array.isArray(searchParams.lakeType)) {
        constraints.push(where('lakeType', 'in', searchParams.lakeType));
      } else {
        constraints.push(where('lakeType', '==', searchParams.lakeType));
      }
    }
    if (searchParams.published && searchParams.published !== 'all') {
      const isPublished = searchParams.published === 'true';
      constraints.push(where('published', '==', isPublished));
    }

    let q;
    if (constraints.length > 0) {
      q = query(lakesRef, ...constraints);
    } else {
      q = query(lakesRef);
    }

    const querySnapshot = await getDocs(q);
    let results = [];
    querySnapshot.forEach((doc) => {
      results.push({ id: doc.id, ...doc.data() });
    });

    // Apply case-insensitive name filter client-side
    if (searchParams.name) {
      const searchTerm = searchParams.name.toLowerCase();
      results = results.filter((lake) => 
        lake.name && lake.name.toLowerCase().includes(searchTerm)
      );
    }

    return { results, searchParams };
  }
);

export const createLake = createAsyncThunk(
  'lakes/createLake',
  async (lakeData) => {
    const lakesRef = collection(db, 'lakes');
    const docRef = await addDoc(lakesRef, lakeData);
    return { id: docRef.id, ...lakeData };
  }
);

export const fetchLakeSpots = createAsyncThunk(
  'lakes/fetchLakeSpots',
  async (lakeId) => {
    const spotsRef = collection(db, 'lake-spots');
    const q = query(spotsRef, where('lakeId', '==', lakeId));
    const querySnapshot = await getDocs(q);
    const results = [];
    querySnapshot.forEach((doc) => {
      results.push({ id: doc.id, ...doc.data() });
    });
    return results;
  }
);

const lakesSlice = createSlice({
  name: 'lakes',
  initialState: {
    lakes: [],
    searchParams: {
      name: '',
      stationId: '',
      lakeType: [],
      published: 'all'
    },
    sortConfig: { key: 'name', direction: 'asc' },
    loading: false,
    error: null,
    searchPerformed: false,
    selectedLake: null,
    lakeSpots: [],
    loadingSpots: false,
  },
  reducers: {
    setSearchParams: (state, action) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    setSortConfig: (state, action) => {
      state.sortConfig = action.payload;
      // Apply sort immediately if we have lakes
      if (state.lakes.length > 0) {
        const { key, direction } = action.payload;
        state.lakes.sort((a, b) => {
          if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
          if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
          return 0;
        });
      }
    },
    setSelectedLake: (state, action) => {
        if (state.selectedLake?.id !== action.payload?.id) {
            state.lakeSpots = [];
        }
        state.selectedLake = action.payload;
    },
    clearSearch: (state) => {
      state.lakes = [];
      state.searchParams = { name: '', stationId: '', lakeType: [], published: 'all' };
      state.searchPerformed = false;
      state.error = null;
      state.selectedLake = null;
      state.lakeSpots = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLakes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLakes.fulfilled, (state, action) => {
        state.loading = false;
        state.lakes = action.payload.results;
        state.searchPerformed = true;
        state.error = null;
        
        // Apply current sort config to new results
        const { key, direction } = state.sortConfig;
        state.lakes.sort((a, b) => {
          if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
          if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
          return 0;
        });
      })
      .addCase(fetchLakes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.searchPerformed = true;
      })
      .addCase(fetchLakeSpots.pending, (state) => {
        state.loadingSpots = true;
      })
      .addCase(fetchLakeSpots.fulfilled, (state, action) => {
        state.loadingSpots = false;
        state.lakeSpots = action.payload;
      })
      .addCase(fetchLakeSpots.rejected, (state, action) => {
        state.loadingSpots = false;
        console.error('Failed to fetch lake spots:', action.error);
      });
  }
});

export const { setSearchParams, setSortConfig, setSelectedLake, clearSearch } = lakesSlice.actions;
export default lakesSlice.reducer;
